import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSubscriptionPlans, getSubscriptionDetails, getSubscriptionStatus, cancelSubscription } from '../services/subscriptionService';
import { setAvailableFeatures } from '../services/featureService';
import { useAppContext } from '../context/AppContext';
import SubscriptionPlanCard from '../components/subscription/SubscriptionPlanCard';
import SubscriptionStatus from '../components/subscription/SubscriptionStatus';

const AdminSubscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { currentUser } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for success message from location state (e.g., after checkout)
    if (location.state?.message) {
      setSuccess(location.state.message);
    }

    // Redirect if not admin
    if (currentUser && !['admin', 'admin'].includes(currentUser.role)) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch subscription plans
        const plansData = await getSubscriptionPlans();
        setPlans(plansData);

        // Try to fetch current subscription status
        try {
          const statusData = await getSubscriptionStatus();
          setSubscription(statusData);
        } catch (statusError) {
          // No active subscription, this is normal for new users
          console.log('No active subscription', statusError);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setError('Failed to load subscription information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchData();
    }
  }, [currentUser, navigate, location.state]);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      return;
    }

    try {
      setLoading(true);
      const result = await cancelSubscription();
      setSubscription(result);
      setSuccess('Your subscription has been canceled and will end on your billing period end date.');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setError('Failed to cancel subscription. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async (updatedSubscription) => {
    setSubscription(updatedSubscription);
    setSuccess('Your subscription has been updated successfully.');

    try {
      const subscriptionDetails = await getSubscriptionDetails();
      if (subscriptionDetails.available_features) {
        setAvailableFeatures(subscriptionDetails.available_features);
      }

    } catch (error) {
      console.error('Error refreshing subscription details:', error);
    }

    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccess('');
    }, 5000);
  };

  if (loading) {
    return <div className="text-center py-10">Loading subscription information...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
        <p className="mt-2 text-sm text-gray-600">Manage your company's Apprecio subscription.</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Show current subscription if exists */}
      {subscription && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Subscription</h2>
          <SubscriptionStatus
            subscription={subscription}
            availablePlans={plans}
            onCancelSubscription={handleCancelSubscription}
            onUpdateSubscription={handleUpdateSubscription}
          />
        </div>
      )}

      {/* Show plans if no active subscription or if subscription is canceled */}
      {(!subscription || subscription.cancel_at_period_end) && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {plans.map(plan => (
              <SubscriptionPlanCard
                key={plan.id}
                plan={plan}
                onSelect={() => navigate(`/admin/subscriptions/checkout/${plan.id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptions;